import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { HOME_TAGLINE } from '@/lib/copy';
import wordmark from '../../assets/brand/logo/wordmark-light.svg';

const KEY = 'ee_age_ack';

/**
 * Storage throws where site data is blocked — a private window in some browsers, Safari with
 * every cookie refused — and a gate that throws takes the whole tree down with it, so an
 * unreadable record reads as "not yet acknowledged" and an unwritable one is simply asked again
 * next visit.
 */
function readAcknowledged(): boolean {
  try {
    return localStorage.getItem(KEY) === 'yes';
  } catch {
    return false;
  }
}

function writeAcknowledged(): void {
  try {
    localStorage.setItem(KEY, 'yes');
  } catch {
    // Asked again next time; nothing else depends on it.
  }
}

/**
 * Adults-only interstitial. Acknowledgement is kept in localStorage rather than a cookie: it
 * is a statement by the visitor, not a session, and nothing on the server depends on it.
 *
 * It is the first thing anyone sees, so it is the brand at full size — the only screen in the
 * app where the wordmark gets to be the hero rather than a mark in a header. It is read
 * synchronously for that reason: a gate decided in an effect paints the feed for one frame
 * first, which is exactly the frame the gate exists to withhold.
 */
export default function AgeGate() {
  const [acknowledged, setAcknowledged] = useState(readAcknowledged);

  // Nothing behind the gate should be reachable while it is up — not by Tab, not by a screen
  // reader's reading order. `inert` is the one attribute that says so, and the shell is the
  // one element it has to be said on.
  useEffect(() => {
    if (acknowledged) return;
    const shell = document.querySelector<HTMLElement>('.ee-shell');
    if (!shell) return;
    shell.setAttribute('inert', '');
    return () => shell.removeAttribute('inert');
  }, [acknowledged]);

  if (acknowledged) return null;

  return (
    <div className="ee-gate" role="dialog" aria-modal="true" aria-label="Adults only">
      <div className="ee-gate__panel">
        <img className="ee-gate__wordmark" src={wordmark} alt="EscortEssential" width={320} height={60} />
        <p className="ee-gate__tagline">{HOME_TAGLINE}</p>

        <p className="ee-gate__eyebrow">Adults only</p>
        <p className="ee-prose">
          This site advertises adult services and is for people aged 18 and over. Everyone
          advertising here has passed an in-person identity and age check.
        </p>

        <div className="ee-gate__actions">
          {/* The dialog has to put focus somewhere, and the one answer most people give is it.
              Without autofocus it started on the document body, behind an inert shell. */}
          <Button
            size="lg"
            variant="solid"
            autoFocus
            onClick={() => {
              writeAcknowledged();
              setAcknowledged(true);
            }}
            data-ee-feature="agegate.enter"
          >
            I am 18 or older
          </Button>
          <Button variant="ghost" asChild data-ee-feature="agegate.leave">
            <a href="https://www.google.com">Leave</a>
          </Button>
        </div>
      </div>
    </div>
  );
}
